import * as React from "react";
import { clsx } from "@plans/appkit";
import * as Tooltip from "@radix-ui/react-tooltip";

/**
 * Tooltip that is toggled both on hover and click.
 */
export function ClickableTooltip({
	trigger,
	children,
	align,
	side,
	alignOffset,
	arrowPadding,
	delayDuration = 200,
	triggerAsChild,
	contentClassName,
	onClick: propsOnClick,
}: {
	trigger: React.ReactNode;
	children: React.ReactNode;
	align?: "start" | "center" | "end";
	side?: "top" | "right" | "bottom" | "left";
	alignOffset?: number;
	arrowPadding?: number;
	delayDuration?: number;
	triggerAsChild?: boolean;
	contentClassName?: string;
	onClick?: (event: React.MouseEvent<HTMLElement>) => void;
}) {
	const triggerRef = React.useRef<HTMLButtonElement>(null);
	const delayedIsOpen = React.useRef(false);

	const onClick = (event: React.MouseEvent<HTMLElement>) => {
		event.preventDefault();

		if (delayedIsOpen.current) {
			propsOnClick?.(event);
		}
	};

	const onOpenChange = (open: boolean) => {
		setTimeout(() => {
			delayedIsOpen.current = open;
		}, delayDuration);
	};

	return (
		<Tooltip.Provider>
			<Tooltip.Root delayDuration={delayDuration} onOpenChange={onOpenChange}>
				<Tooltip.Trigger
					type="button"
					onClick={onClick}
					className={triggerAsChild ? undefined : "global-focus rounded-xs"}
					style={{ textAlign: "inherit" }}
					ref={triggerRef}
					asChild={triggerAsChild}
				>
					{trigger}
				</Tooltip.Trigger>
				<Tooltip.Portal>
					<Tooltip.Content
						className="z-(--z-tooltip)"
						sideOffset={4}
						alignOffset={alignOffset}
						align={align}
						side={side}
						arrowPadding={arrowPadding}
						collisionPadding={12}
						onPointerDownOutside={(event) => {
							// Don't close the tooltip if the trigger is clicked - see
							// https://github.com/radix-ui/primitives/blob/main/packages/react/tooltip/src/Tooltip.stories.tsx#L659-L687
							if (
								event.target === triggerRef.current ||
								triggerRef.current?.contains(event.target as HTMLElement)
							) {
								event.preventDefault();
							}
						}}
					>
						<Tooltip.Arrow className="fill-gray-900" />
						<span
							className={clsx(
								"block rounded-sm bg-gray-900 px-3 py-2.5 text-xs text-white",
								contentClassName ?? "max-w-[20em]",
							)}
						>
							{children}
						</span>
					</Tooltip.Content>
				</Tooltip.Portal>
			</Tooltip.Root>
		</Tooltip.Provider>
	);
}
